import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  getAdditionalUserInfo,
  type UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, initializeEssentialServices, setKeepSignedIn } from '../config/firebase';
import { offlineService } from '../services/offlineService';
import { createComponentLogger } from '../utils/logger';
import { withErrorHandling } from '../utils/errorHandler';
import { appConfig } from '../config/appConfig';
import { requiresConsentFlag } from '../utils/age';
import { CacheUtils } from '../utils/cache';
import { lockForChildAccount, unlockForAdultAccount } from '../utils/trackingConsent';

/**
 * Take one child's things off this device (S223, S232): their unsaved drafts are sent first, and
 * only if every one landed are the local copies removed. A draft that could not be sent is the
 * only copy of the child's work; it stays, scoped to them, for their next sign-in.
 *
 * After `signOut` (the session-security paths) the draft push is refused by the rules — those
 * drafts are kept, which is the right failure.
 */
async function leaveDevice(uid: string): Promise<void> {
  const { failed } = await offlineService
    .pushDrafts(uid)
    .catch(() => ({ pushed: 0, failed: 1 }));
  if (failed > 0) {
    logger.warn(`${failed} unsaved draft(s) could not be sent; keeping them on this device`);
  }
  await offlineService
    .clearUserData(uid, { keepDrafts: failed > 0 })
    .catch((err) => logger.warn("Could not clear this device's local copies on sign-out", err));
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, birthDate: string) => Promise<void>;
  logout: () => Promise<void>;
  // #46: returns { isNewUser } so callers can route first-time federated users
  // through the ToS/Privacy acceptance flow.
  loginWithGoogle: () => Promise<{ isNewUser: boolean } | void>;
  loginWithMicrosoft: () => Promise<{ isNewUser: boolean } | void>;
  resetPassword: (email: string) => Promise<void>;
}

// District deployments can lock federated sign-in to their own domain(s) by
// setting REACT_APP_ALLOWED_AUTH_DOMAINS (comma-separated). Empty = allow all.
const ALLOWED_AUTH_DOMAINS = (process.env.REACT_APP_ALLOWED_AUTH_DOMAINS || '')
  .split(',')
  .map((d) => d.trim().toLowerCase())
  .filter(Boolean);

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const logger = createComponentLogger('AuthContext');

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize essential Firebase services on first use
    const { auth: authService } = initializeEssentialServices();
    
    /*
     * S232: every sign-out leaves the device clean, not only the Sign out button. Inactivity,
     * the maximum session length and a failed token check sign out through `sessionSecurity`,
     * straight to Firebase — and on a shared classroom Chromebook those are how most sessions
     * end. The transition from a signed-in child to nobody is the one place all of them pass.
     */
    let previousUid: string | null = null;
    const unsubscribe = onAuthStateChanged(authService, (user) => {
      if (previousUid && !user) {
        // S233: forgotten NOW, not after the draft push — a child signing in before that finished
        // had their own "keep me signed in" choice overwritten.
        void setKeepSignedIn(false).catch(() => undefined);
        CacheUtils.clearAll();
        void leaveDevice(previousUid);
      }
      // S232: the analytics lock follows the session on EVERY route, public ones included
      // (`/play/:id`), not only where `useOnboardingStatus` is mounted. Signed in = locked until
      // a read proves an adult; signed out = the viewer's own choice back.
      if (user) lockForChildAccount();
      else unlockForAdultAccount();
      previousUid = user?.uid ?? null;
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  /**
   * Create an account.
   *
   * `birthDate` (ISO `YYYY-MM-DD`) is REQUIRED as of 2026-09-05. Audit finding
   * C3 break #1: no age was collected anywhere, so COPPA could not be applied
   * to anyone. `requiresParentalConsent` is derived here and written once;
   * `firestore.rules` then rejects any client write that changes either field,
   * so a child cannot age themselves out of the protection.
   *
   * `isUnder13` fails closed — an unparseable date yields `true`.
   */
  const register = async (email: string, password: string, birthDate: string) => {
    return withErrorHandling(async () => {
      // Create the user account
      const userCredential = await createUserWithEmailAndPassword(auth.value, email, password);
      const user = userCredential.user;

      const requiresParentalConsent = requiresConsentFlag(birthDate);

      // Create user profile in Firestore
      const userDocRef = doc(db.value, 'users', user.uid);
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || email.split('@')[0],
        birthDate,
        requiresParentalConsent,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        settings: {
          theme: 'light',
          autoSave: true
        }
      });

      // S135: nothing in the product had EVER sent a verification email. The
      // app ships an EmailVerificationBanner, a resend screen and a login gate
      // for a message that was never written — and the login gate signs the
      // user out, so `resendEmailVerification` (which needs a signed-in user)
      // could not have rescued them either. Turning the flag on without this
      // would have locked out every account that ever registered.
      //
      // Best effort by design: a mail failure must not undo an account that has
      // already been created, or the child is left with a Firebase Auth user,
      // no profile document, and an email address that now says "in use". It is
      // logged, and the banner plus the resend screen are the recovery path.
      try {
        await sendEmailVerification(user);
      } catch (verificationError) {
        logger.error('Account created but the verification email failed', verificationError);
      }

      logger.debug('User registered successfully:', user.uid, {
        requiresParentalConsent,
      });
    }, 'user registration');
  };

  const login = async (email: string, password: string) => {
    return withErrorHandling(async () => {
      const userCredential = await signInWithEmailAndPassword(auth.value, email, password);
      const user = userCredential.user;

      // S135: the email-verification gate existed — in `authService.signIn`,
      // which NOTHING calls. `Login` uses this context, and the two files that
      // import `authService` use other methods entirely, so the check sat in
      // unreachable code while the live sign-in path had none. Three ends of one
      // feature, none of them joined: a banner, a resend screen and a gate, for
      // a message nothing sent, guarding a function nobody called.
      if (!user.emailVerified && appConfig.isFeatureEnabled('requireEmailVerification')) {
        // Send a fresh link before letting go of the session: resending needs a
        // signed-in user, and the sign-out below removes one.
        try {
          await sendEmailVerification(user);
        } catch (verificationError) {
          logger.error('Could not send a verification email during login', verificationError);
        }
        await signOut(auth.value);
        throw new Error(
          'Please verify your email before signing in. We have sent you a new link.'
        );
      }

      // Check if user profile exists in Firestore
      const userDocRef = doc(db.value, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // If profile doesn't exist, create it (for legacy users)
      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || email.split('@')[0],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          settings: {
            theme: 'light',
            autoSave: true
          }
        });
      }
      
      logger.debug('User logged in successfully:', user.uid);
    }, 'user login');
  };

  // #46 fix (2026-05-17): record terms/privacy acceptance for Google sign-up.
  // Previously, email/password Register went through a UI that captured the
  // ToS checkbox state, but Google login skipped this entirely — first-time
  // Google users became active accounts with NO record of consent. Now we
  // detect first-time vs returning Google user via getAdditionalUserInfo,
  // and the *caller* of loginWithGoogle is responsible for showing a ToS
  // dialog when isNewUser is true. We persist `termsAcceptedAt` and
  // `privacyAcceptedAt` timestamps on the user doc so audits can verify
  // consent provenance. The function returns the isNewUser flag so the
  // calling Login/Register component can route appropriately.
  // Shared post-sign-in provisioning for any federated provider (Google,
  // Microsoft, …). Enforces the optional district-domain allowlist, then
  // creates the Firestore profile for first-time users with ToS PENDING — the
  // caller MUST present the ToS/Privacy dialog and call recordTermsAcceptance()
  // before letting an isNewUser into the app (#46).
  const provisionFederatedUser = async (
    userCredential: UserCredential
  ): Promise<{ isNewUser: boolean }> => {
    const user = userCredential.user;

    // District-domain lock (no-op unless REACT_APP_ALLOWED_AUTH_DOMAINS is set).
    if (ALLOWED_AUTH_DOMAINS.length > 0) {
      const domain = (user.email || '').split('@')[1]?.toLowerCase() || '';
      if (!ALLOWED_AUTH_DOMAINS.includes(domain)) {
        await signOut(auth.value);
        throw new Error(
          `Sign-in is restricted to ${ALLOWED_AUTH_DOMAINS.join(', ')}. ` +
            `The account ${user.email || ''} is not permitted.`
        );
      }
    }

    // getAdditionalUserInfo replaces the deprecated userCredential.additionalUserInfo.
    const additionalInfo = getAdditionalUserInfo(userCredential);
    const isNewUser = additionalInfo?.isNewUser ?? false;

    const userDocRef = doc(db.value, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0],
        photoURL: user.photoURL,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        termsAcceptedAt: null, // explicit null — must be filled by ToS dialog
        privacyAcceptedAt: null,
        settings: {
          theme: 'light',
          autoSave: true,
        },
      });
    }
    return { isNewUser };
  };

  const loginWithGoogle = async (): Promise<{ isNewUser: boolean }> => {
    return withErrorHandling(async () => {
      const provider = new GoogleAuthProvider();
      // Always let the user pick an account; if the district configured exactly
      // one allowed domain, hint Google to that Workspace domain.
      provider.setCustomParameters({
        prompt: 'select_account',
        ...(ALLOWED_AUTH_DOMAINS.length === 1 ? { hd: ALLOWED_AUTH_DOMAINS[0] } : {}),
      });
      const userCredential = await signInWithPopup(auth.value, provider);
      const result = await provisionFederatedUser(userCredential);
      logger.debug('Google login successful:', userCredential.user.uid, 'isNewUser:', result.isNewUser);
      return result;
    }, 'Google login');
  };

  const loginWithMicrosoft = async (): Promise<{ isNewUser: boolean }> => {
    return withErrorHandling(async () => {
      // Microsoft Entra ID (Azure AD) via Firebase's generic OIDC provider.
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({ prompt: 'select_account' });
      const userCredential = await signInWithPopup(auth.value, provider);
      const result = await provisionFederatedUser(userCredential);
      logger.debug('Microsoft login successful:', userCredential.user.uid, 'isNewUser:', result.isNewUser);
      return result;
    }, 'Microsoft login');
  };

  const logout = async () => {
    return withErrorHandling(async () => {
      /*
       * S223: leave nothing of this child on the device. Pending offline changes get one chance to
       * reach the server while the child is still signed in; then their local copies are removed,
       * and "keep me signed in" is forgotten so the next person on this device starts unticked.
       */
      //
      // S232: "one chance to reach the server" was `syncToCloud`, whose queue nothing fills, so the
      // drafts a failed autosave left behind were deleted unsent. They are pushed now; any that
      // cannot be sent stay on this device (scoped to this child) rather than being destroyed.
      const uid = auth.value.currentUser?.uid;
      if (uid) await leaveDevice(uid);
      await signOut(auth.value);
      await setKeepSignedIn(false).catch(() => undefined);
      // Shared classroom devices: whatever is left in the in-memory caches
      // after sign-out is readable by the next child to sign in on the same
      // browser. Cleared here as well as scoping the keys per user.
      CacheUtils.clearAll();
      logger.debug('User logged out successfully');
    }, 'user logout');
  };

  const resetPassword = async (email: string) => {
    return withErrorHandling(async () => {
      await sendPasswordResetEmail(auth.value, email);
      logger.debug('Password reset email sent');
    }, 'password reset');
  };

  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithMicrosoft,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};